import type { CSSProperties } from 'react';

// A placeholder ad/partner slot — the "your creative goes here" box in the sidebar rail and the
// Home header. Purely presentational for now (no ad network); when one lands, the creative renders
// in place of the hatched box behind the same frame.
//   • rail   — tall sidebar skyscraper; grows to fill the space it's given.
//   • banner — a slim Home strip; fills its container's height (sized by the greeting beside it).
interface SponsorSlotProps {
  variant: 'rail' | 'banner';
  className?: string;
}

// Diagonal hatch marking the empty slot, so it reads as an intentional placeholder rather than a
// broken image. Uses the theme's hairline token so it tracks the active palette.
const HATCH: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(-45deg, var(--line-2) 0, var(--line-2) 1px, transparent 1px, transparent 9px)',
};

export function SponsorSlot({ variant, className }: SponsorSlotProps) {
  // Banner: a compact single-row strip that fills its parent's height (the Home header sets that to
  // the greeting's height via items-stretch), so it never towers over "Good morning …".
  if (variant === 'banner') {
    return (
      <div className={'flex flex-col rounded border border-line bg-surface p-2 ' + (className ?? '')}>
        <div className="flex flex-1 items-center justify-between gap-3 rounded-sm border border-line px-3 py-1.5" style={HATCH}>
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-2">Sponsored</span>
          <span className="rounded-sm bg-surface px-2 py-0.5 font-mono text-[11px] text-muted">creative · 728 × 90</span>
          {/* Ad-choices affordance placeholder — wired up when a real ad network lands. */}
          <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] border border-line" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // Rail: a flex column so its creative box stretches to whatever vertical space the sidebar gives it.
  // (Real partners render through SponsorRail instead; this is the empty-slot fallback.)
  return (
    <div className={'flex flex-col rounded border border-line bg-surface p-3 ' + (className ?? '')}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-2">Sponsored</span>
        <span className="h-3.5 w-3.5 rounded-[3px] border border-line" aria-hidden="true" />
      </div>
      <div className="flex min-h-[120px] flex-1 items-center justify-center rounded-sm border border-line" style={HATCH}>
        <span className="rounded-sm bg-surface px-2 py-0.5 font-mono text-[11px] text-muted">creative · 240 × 600</span>
      </div>
      <p className="mt-2 text-[11px] leading-snug text-muted">
        Your partner spot — gear, courses &amp; federation offers for referees.
      </p>
    </div>
  );
}
